"use client";

import { useEffect, useRef, useState } from "react";

import {
  BORDER,
  FONT_MONO,
  TEXT_MUTE,
  VIOLET_BRIGHT,
} from "@/components/ui/organisms/chord-practice/palette";
import { rungWords } from "@/lib/levels/words";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { ARRIVE, EASE_IN, EASE_OUT } from "../motion";

/**
 * Les deux parcours du morceau, dans la langue de la fiche.
 *
 * ## Ce que la fiche déplie, et non ce qu'un autre écran apporte
 *
 * Ce bloc a été une page à lui seul, l'entrée du programme, avec son fond, son
 * surtitre, sa titraille et ses colonnes. Elle doublait la fiche : même titre,
 * même compositeur, même tonalité, et la fiche portait déjà « Apprendre ». Le
 * choix vit donc dans la fiche, et il n'y a plus qu'un écran.
 *
 * Il ne s'y installe pas non plus avec ses meubles. Rien ici ne vient du sas :
 * ni ses colonnes, ni son filet, ni son intertitre, qui est d'ailleurs à un
 * autre violet. Deux motifs de la fiche suffisaient, et ils étaient déjà là.
 *
 * **La ligne d'action, dépliée.** La fiche pose déjà deux gestes centrés,
 * séparés par un filet vertical sur l'axe. L'état de choix ne fait que les
 * écarter : chacun monte de rang, gagne un nom au-dessus et une phrase entre
 * les deux, et le filet de douze pixels devient celui qui sépare les colonnes,
 * sur la même verticale.
 *
 * **Le nom au-dessus, et non en dessous.** La fiche a deux places pour une
 * petite capitale monospace, et elles ne sont pas interchangeables : en
 * dessous quand elle légende une **valeur** — « Sol min » a besoin qu'on lui
 * dise que c'est la tonalité — et au-dessus quand elle nomme un **bloc**, comme
 * « DIFFICULTÉ PAR MESURE » au-dessus des barres. Un parcours n'est pas une
 * valeur à légender.
 *
 * ## Tout est centré, y compris les phrases
 *
 * La fiche ne connaît pas l'alignement à gauche, sauf le mot « MORCEAU » de son
 * surtitre. Les proses sont donc centrées et bornées à leur mesure de lecture :
 * trois lignes de quarante-cinq signes, ce qui se lit sans effort à un bras de
 * distance, et c'est le prix pour que ça reste la fiche.
 *
 * ## Les deux se ressemblent, et c'est le sujet
 *
 * Même nom, même corps, même geste. L'écran ne fait qu'une chose : poser un
 * choix, et deux offres qui ne se présentent pas pareil ne se comparent pas.
 * Le parcours harmonique a longtemps eu un mot violet deux fois plus grand et
 * l'autre un lien gris ; la forme répondait alors à la place du joueur, alors
 * qu'apprendre un morceau par cœur est un projet entier. Il ne reste qu'une
 * suggestion, l'ordre de lecture, et elle suffit.
 */
export function PieceProgram({
  shown,
  rungIds,
  busy,
  onPickRung,
  onByHeart,
}: {
  /** Le choix est ouvert. Faux pendant qu'il se replie. */
  readonly shown: boolean;
  /**
   * L'échelle, dans l'ordre, ou `null` quand ce morceau n'a pas d'analyse.
   *
   * Le parcours harmonique fabrique ses niveaux à partir d'elle : sans analyse
   * il n'existe pas, et la colonne ne se montre pas. Offrir une porte qui ne
   * s'ouvre pas est pire que n'en offrir qu'une.
   */
  readonly rungIds: readonly string[] | null;
  /** Un niveau est en train de se poser sous les mains. */
  readonly busy: boolean;
  readonly onPickRung: (index: number) => void;
  readonly onByHeart: () => void;
}) {
  /*
    Le dépli, et l'état de l'écran au moment où il a été ouvert.

    Les deux dans le même état parce qu'ils changent ensemble : le dépli ne
    survit pas au repli de l'écran, puisque revenir à la fiche puis rouvrir le
    choix, c'est reposer la question et non reprendre où l'on en était.

    Ajusté pendant le rendu plutôt que dans un effet, ce qui est le motif de la
    maison pour un état qui suit une propriété : un effet le remettrait à jour
    une image trop tard, et l'écran montrerait la liste dépliée le temps de
    cette image-là.
  */
  const [picking, setPicking] = useState({ shown, open: false });

  if (picking.shown !== shown) {
    setPicking({ shown, open: false });
  }

  const pickingRung = picking.shown === shown && picking.open;
  const two = rungIds !== null;

  return (
    <section
      aria-label="Choisir un parcours"
      className="@container"
      style={{ width: "100%", maxWidth: BLOCK_MAX_PX, margin: "0 auto" }}
    >
      <div style={{ position: "relative" }}>
        {two && <AxisRule shown={shown} />}

        <div
          style={{
            display: "grid",
            // Une piste ou deux, selon la place, et sans palier nommé : le
            // pliage se lit sur la **boîte** et non sur la fenêtre, parce que
            // cet écran vit dans le cadre que le studio lui laisse et que ce
            // cadre change quand on ouvre le panneau de gauche.
            gridTemplateColumns: two
              ? `repeat(auto-fit, minmax(min(100%, ${COLUMN_MIN_PX}px), 1fr))`
              : "1fr",
            gap: `36px ${GUTTER_PX}px`,
          }}
        >
          {two && (
            <Path
              name="Le parcours harmonique"
              delay={ARRIVE.left}
              shown={shown}
              prose={
                "Tu apprends ce morceau par ce qui le tient : ses accords, un niveau à la fois, " +
                "du modèle le plus simple jusqu'à la partition entière."
              }
              action="Commencer"
              ariaLabel="Commencer le parcours harmonique"
              hint="Tu démarres au premier niveau, et tu montes à ton rythme."
              disabled={busy}
              first
              onAct={() => onPickRung(0)}
              below={
                <RungToggle
                  open={pickingRung}
                  onToggle={() => setPicking((was) => ({ shown, open: !was.open }))}
                />
              }
            />
          )}

          <Path
            name="Le parcours des notes"
            delay={two ? ARRIVE.right : ARRIVE.left}
            shown={shown}
            prose={
              "Tu apprends les notes de ce morceau, phrase par phrase, main après main, " +
              "jusqu'à ce qu'il tienne sous les doigts."
            }
            action="Commencer"
            ariaLabel="Commencer le parcours des notes"
            hint="Tu retrouves le morceau tel qu'il est écrit."
            first={!two}
            onAct={onByHeart}
          />
        </div>
      </div>

      {rungIds !== null && (
        <RungStrip open={pickingRung} rungIds={rungIds} busy={busy} onPick={onPickRung} />
      )}
    </section>
  );
}

/**
 * La largeur du bloc, au-delà de laquelle les deux colonnes s'éloignent trop.
 *
 * La fiche est centrée : ses colonnes ne peuvent pas tenir les deux bords de la
 * pièce comme celles du sas, sinon elles laisseraient un trou sous la titraille
 * et casseraient l'axe. Elles s'écartent donc jusqu'ici, et pas plus loin.
 */
const BLOCK_MAX_PX = 860;

/** Sous cette largeur, une colonne cesse d'en être une. */
const COLUMN_MIN_PX = 300;

/** L'air entre les deux, qui porte le filet. */
const GUTTER_PX = 72;

/**
 * Un parcours : son nom, ce qu'il est, le geste qui l'ouvre et sa promesse.
 *
 * La légende sous le geste rend le choix honnête : les deux ne mènent pas au
 * même endroit, et aucun des deux ne doit surprendre.
 */
function Path({
  name,
  prose,
  action,
  ariaLabel,
  hint,
  disabled = false,
  first = false,
  onAct,
  below = null,
  delay,
  shown,
}: {
  readonly name: string;
  readonly prose: string;
  readonly action: string;
  readonly ariaLabel: string;
  readonly hint: string;
  readonly disabled?: boolean;
  /** Celui que le focus vise quand le choix s'ouvre. */
  readonly first?: boolean;
  readonly onAct: () => void;
  /** Ce qui se range sous la légende, ou `null`. */
  readonly below?: React.ReactNode;
  readonly delay: number;
  readonly shown: boolean;
}) {
  const reduced = useReducedMotion();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        opacity: shown ? 1 : 0,
        transform: shown || reduced ? "none" : "translateY(16px)",
        transition: reduced
          ? "none"
          : shown
            ? `opacity 400ms ${delay}ms ${EASE_OUT}, transform 400ms ${delay}ms ${EASE_OUT}`
            : `opacity 160ms 0ms ${EASE_IN}, transform 160ms 0ms ${EASE_IN}`,
      }}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12,
          lineHeight: "16px",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: VIOLET_BRIGHT,
        }}
      >
        {name}
      </div>

      <p
        style={{
          margin: "14px 0 0",
          maxWidth: 340,
          fontFamily: "Inter, sans-serif",
          fontSize: 14,
          lineHeight: 1.6,
          color: "var(--color-text-secondary)",
        }}
      >
        {prose}
      </p>

      <div style={{ marginTop: 22 }}>
        <Act
          label={action}
          ariaLabel={ariaLabel}
          disabled={disabled}
          first={first}
          onClick={onAct}
        />
      </div>

      <div
        style={{
          marginTop: 9,
          maxWidth: 300,
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--color-text-soft)",
        }}
      >
        {hint}
      </div>

      {below}
    </div>
  );
}

/**
 * Le geste d'un parcours : un mot et sa flèche.
 *
 * C'est « Apprendre → » de la ligne d'action, monté de dix-sept à vingt
 * pixels : il vient de devenir le sujet de l'écran. Rien d'autre ne change, ni
 * l'encre, ni la graisse, ni la flèche qui avance de deux pixels — le signe qui
 * dit « ça mène quelque part » sans rien ajouter à l'écran.
 *
 * Un mot coloré et non une pastille pleine ni un contour : le produit n'en
 * dessine nulle part, et le violet se donne pour cliquable par lui seul dans
 * une page où presque rien ne l'est.
 */
function Act({
  label,
  ariaLabel,
  disabled,
  first,
  onClick,
}: {
  readonly label: string;
  /**
   * Le nom entier du geste.
   *
   * Deux colonnes qui offrent chacune « Commencer » se distinguent à l'oeil par
   * leur nom, juste au-dessus ; une liste de gestes lue à voix haute, elle, n'a
   * pas cette colonne.
   */
  readonly ariaLabel: string;
  readonly disabled: boolean;
  /** La fiche pose le focus dessus quand le choix s'ouvre. */
  readonly first: boolean;
  readonly onClick: () => void;
}) {
  const nudge = (target: HTMLElement, by: string) => {
    const arrow = target.querySelector<HTMLSpanElement>("[data-arrow]");

    if (arrow !== null) {
      arrow.style.transform = `translateX(${by})`;
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      data-first-action={first ? "" : undefined}
      className="inline-flex items-center transition-[filter] duration-200 hover:brightness-125 focus-visible:brightness-125 disabled:cursor-default disabled:opacity-50 motion-reduce:transition-none"
      style={{
        gap: 8,
        padding: 0,
        border: "none",
        background: "transparent",
        color: "var(--color-accent-bright)",
        fontFamily: "Inter, sans-serif",
        fontSize: 20,
        fontWeight: 600,
        letterSpacing: "-0.005em",
        cursor: "pointer",
      }}
      onMouseEnter={(event) => nudge(event.currentTarget, "2px")}
      onMouseLeave={(event) => nudge(event.currentTarget, "0")}
      onFocus={(event) => nudge(event.currentTarget, "2px")}
      onBlur={(event) => nudge(event.currentTarget, "0")}
    >
      {label}
      <span
        aria-hidden
        data-arrow
        className="inline-block transition-transform duration-200 motion-reduce:transition-none"
      >
        →
      </span>
    </button>
  );
}

/**
 * Le filet qui sépare les deux colonnes.
 *
 * Sur l'axe exact que le filet de douze pixels de la ligne d'action vient de
 * libérer. Le séparateur ne disparaît pas, il change d'échelle et de rôle sur
 * la même verticale, et c'est ce qui coud les deux états.
 *
 * Il s'allume par sa hauteur, depuis son milieu, quand la place est faite.
 */
function AxisRule({ shown }: { readonly shown: boolean }) {
  const reduced = useReducedMotion();

  return (
    <span
      aria-hidden
      // 672 px, c'est-à-dire `COLUMN_MIN_PX * 2 + GUTTER_PX` : en dessous,
      // `auto-fit` a replié la grille sur une piste, le filet n'a plus d'axe à
      // occuper et une colonne seule n'a rien à séparer. Le nombre est écrit en
      // toutes lettres parce que Tailwind lit le texte source et ne saurait pas
      // fabriquer la classe depuis les deux constantes.
      className="pointer-events-none absolute inset-y-0 left-1/2 block w-px @max-[672px]:hidden"
      style={{
        background: `linear-gradient(180deg, transparent 0%, ${BORDER} 22%, ${BORDER} 78%, transparent 100%)`,
        transform: shown || reduced ? "scaleY(1)" : "scaleY(0)",
        opacity: shown ? 1 : 0,
        transition: reduced
          ? "none"
          : shown
            ? `transform ${ARRIVE.rule.ms}ms ${ARRIVE.rule.delay}ms ${EASE_OUT}, opacity ${ARRIVE.rule.ms}ms ${ARRIVE.rule.delay}ms ${EASE_OUT}`
            : `opacity 160ms 0ms ${EASE_IN}`,
      }}
    />
  );
}

/**
 * Le geste qui déplie l'échelle, sous la colonne de son parcours.
 *
 * L'écran a longtemps posé l'échelle entière à la place du bouton. Le choix
 * devenait alors « quel niveau », ce qui demande de savoir ce que sont les
 * niveaux, avant même d'avoir choisi un chemin. Repliée, elle ne coûte rien à
 * qui arrive et ne coûte qu'un geste à qui revient et sait où il en est.
 *
 * Il reste sous ce parcours-ci parce que les niveaux se choisissent là où on
 * les prend. La liste, elle, se déroule plus bas, sous les deux colonnes : voir
 * `RungStrip`.
 */
function RungToggle({
  open,
  onToggle,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls="piece-program-rungs"
      className="mt-4 inline-flex items-center rounded-md transition-opacity duration-150 hover:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none"
      style={{
        gap: 7,
        padding: "2px 4px",
        border: "none",
        background: "transparent",
        // L'encre de « Explorer → », l'autre geste discret de la fiche.
        color: "var(--color-accent-bright)",
        opacity: 0.7,
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {open ? "Masquer les niveaux" : "Choisir un niveau"}

      <span
        aria-hidden
        className="inline-block transition-transform duration-200 motion-reduce:transition-none"
        style={{ transform: open ? "rotate(180deg)" : "none", fontSize: 9 }}
      >
        ▾
      </span>
    </button>
  );
}

/**
 * L'échelle du morceau, en une table de rangs sous les deux colonnes.
 *
 * ## Une table, et non une file
 *
 * Ces niveaux ont d'abord repris le motif de suite ordonnée de la fiche : la
 * progression d'accords, une file horizontale séparée par des points médians.
 * Le motif ne se transporte pas. `i · VI · V` tient parce qu'un chiffrage est
 * un signe de trois lettres, là où « Notes étrangères » en fait seize : la file
 * passait à la ligne au milieu d'elle-même, laissait un point médian pendre en
 * fin de ligne, et recentrait le reste sous le vide. Neuf mots flottants, sans
 * rang, sans colonne, et rien qui dise que c'est une montée.
 *
 * Le rang est donc écrit. `01` à `09` dans la petite chasse fixe des légendes
 * de la fiche cale une colonne de chiffres qui tient la table debout, et dit
 * l'ordre au lieu de le laisser déduire du sens de lecture. Trois colonnes
 * quand la boîte les porte, deux, puis une : les lignes s'alignent entre elles
 * au lieu de se centrer chacune pour soi, et la liste ne peut plus se casser en
 * pyramide sur son dernier tiers.
 *
 * ## Ce que la table ajoute, et que la file ne pouvait pas
 *
 * Le rang survolé, ou atteint au clavier, écrit sa promesse sous la table, dans
 * les mots de `rungWords`. Choisir « Voicings réels » demandait jusqu'ici de
 * savoir ce que c'est ; la phrase le dit à sa place. Sa hauteur lui est
 * réservée pour que rien ne saute quand elle arrive.
 *
 * Le doigt, lui, n'a rien à survoler : il touche un rang et part. La phrase est
 * un supplément de la souris, jamais une condition du choix, et c'est pourquoi
 * elle ne porte aucune information qui manquerait au nom.
 *
 * ## Sous les deux colonnes, et non sous l'une d'elles
 *
 * Neuf niveaux empilés sous la colonne de gauche la faisaient grandir seule, et
 * la ressemblance des deux parcours, qui est tout le sujet de l'écran, ne
 * survivait pas à l'ouverture. En pleine largeur, elle ne déséquilibre rien.
 *
 * La table vient sous les yeux sans se donner de hauteur : la page défile déjà,
 * et un second défilement dans le premier demanderait au doigt de deviner
 * lequel il pousse. Ouvrir la remonte donc dans la vue, ce qui est le geste
 * qu'on aurait fait à sa place.
 */
function RungStrip({
  open,
  rungIds,
  busy,
  onPick,
}: {
  readonly open: boolean;
  readonly rungIds: readonly string[];
  readonly busy: boolean;
  readonly onPick: (index: number) => void;
}) {
  const strip = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();
  /*
    Le rang que la souris ou le clavier tient, et l'état de la table au moment
    où il a été pris.

    Les deux dans le même état, et ajusté pendant le rendu, comme le dépli
    au-dessus : le rang lu ne survit pas au repli, puisque rouvrir la table
    c'est la relire depuis le début, et une phrase posée sous une table vide
    n'aurait plus de rang à qui appartenir.
  */
  const [read, setRead] = useState<{ open: boolean; at: number | null }>({
    open,
    at: null,
  });

  if (read.open !== open) {
    setRead({ open, at: null });
  }

  const reading = read.open === open ? read.at : null;

  useEffect(() => {
    if (open) {
      strip.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const held = reading === null ? null : rungWords(rungIds[reading]);

  return (
    <div
      id="piece-program-rungs"
      ref={strip}
      style={{
        marginTop: 30,
        animation: reduced ? undefined : "piece-overview-fadeup 400ms 60ms ease both",
      }}
    >
      <div
        // Le pliage se lit sur la boîte et non sur la fenêtre, comme celui des
        // deux colonnes au-dessus : cet écran vit dans le cadre que le studio
        // lui laisse, et ce cadre change quand on ouvre le panneau de gauche.
        className="grid grid-cols-1 @min-[440px]:grid-cols-2 @min-[672px]:grid-cols-3"
        style={{
          width: "100%",
          maxWidth: LADDER_MAX_PX,
          margin: "0 auto",
          columnGap: 24,
          rowGap: 2,
        }}
      >
        {rungIds.map((id, at) => {
          const words = rungWords(id);
          const lit = reading === at;
          const rank = `Niveau ${at + 1}, ${words.label}`;

          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(at)}
              disabled={busy}
              aria-label={words.summary === "" ? rank : `${rank} : ${words.summary}`}
              onMouseEnter={() => setRead({ open, at })}
              onMouseLeave={() => setRead((was) => (was.at === at ? { open, at: null } : was))}
              onFocus={() => setRead({ open, at })}
              onBlur={() => setRead((was) => (was.at === at ? { open, at: null } : was))}
              className="flex items-baseline rounded-md transition-colors duration-150 disabled:cursor-default motion-reduce:transition-none"
              style={{
                gap: 12,
                padding: "9px 8px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.45 : 1,
                // L'encre passe par le style et non par une classe `hover:` :
                // la couleur de base est déjà en ligne, et une classe perdrait
                // contre elle sans rien dire.
                color: lit ? "#fff" : "var(--color-text-secondary)",
              }}
            >
              <span
                aria-hidden
                className="transition-colors duration-150 motion-reduce:transition-none"
                style={{
                  flex: "none",
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  letterSpacing: "0.1em",
                  color: lit ? VIOLET_BRIGHT : TEXT_MUTE,
                }}
              >
                {String(at + 1).padStart(2, "0")}
              </span>

              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                {words.label}
              </span>
            </button>
          );
        })}
      </div>

      <p
        // Le nom du rang porte déjà sa phrase dans son `aria-label` : la lire
        // deux fois ferait dire à la table qu'il y a là deux choses à entendre.
        aria-hidden
        style={{
          margin: "16px auto 0",
          maxWidth: 520,
          minHeight: 38,
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: "center",
          color: "var(--color-text-soft)",
          opacity: held === null || held.summary === "" ? 0 : 1,
          transition: reduced ? "none" : `opacity 200ms ${EASE_OUT}`,
        }}
      >
        {held?.summary ?? ""}
      </p>
    </div>
  );
}

/**
 * La largeur de la table.
 *
 * Un rang et son chiffre tiennent dans deux cents pixels, y compris le plus
 * long ; les écarter au-delà en ferait trois listes voisines plutôt qu'une
 * suite, et la colonne de chiffres cesserait de se lire comme une montée.
 */
const LADDER_MAX_PX = 620;
